import {
  UserManager,
  WebStorageStateStore,
  type User,
  type UserManagerSettings,
} from 'oidc-client-ts';

/**
 * Console OIDC config — read at module load from Vite env. Mirrors the
 * metaphysics frontend pattern but reads from `import.meta.env` instead of a
 * server `/auth/config` endpoint (the cortex API doesn't expose one yet).
 */
export interface IOidcConfig {
  authority: string;
  clientId: string;
}

const env = import.meta.env;
// Hosted dev Keycloak is the default — set VITE_KEYCLOAK_AUTHORITY to point
// at a local Keycloak (e.g. http://localhost:3002/realms/formant) for local-
// stack development. lib/keycloak-admin.ts derives the admin-API base from
// this same env so the two stay in sync.
export const oidcConfig: IOidcConfig = {
  authority: (
    env.VITE_KEYCLOAK_AUTHORITY ?? 'https://keycloak.cortex-dev.formant.ai/realms/formant'
  ).replace(/\/$/, ''),
  clientId: env.VITE_KEYCLOAK_CLIENT_ID ?? 'cortex-console',
};

let manager: UserManager | null = null;

function settings(cfg: IOidcConfig): UserManagerSettings {
  const origin = window.location.origin;
  return {
    authority: cfg.authority,
    client_id: cfg.clientId,
    redirect_uri: `${origin}/`,
    post_logout_redirect_uri: `${origin}/`,
    response_type: 'code',
    scope: 'openid profile email organization',
    // Auto-refresh via the refresh-token grant ~60s before the access token
    // expires. No iframe-based silent renew (Keycloak's iframe flow is finicky
    // behind same-origin checks); the refresh-token grant is plain HTTP.
    automaticSilentRenew: true,
    accessTokenExpiringNotificationTimeInSeconds: 60,
    loadUserInfo: true,
    // Persist tokens to localStorage so the session survives tab close + can
    // be captured by Playwright's storageState for E2E auth reuse. Default
    // (sessionStorage) drops on tab close, which forces a full OIDC redirect
    // every time and makes test runs flaky around mid-flow re-auth.
    userStore: new WebStorageStateStore({ store: window.localStorage }),
  };
}

export function getUserManager(): UserManager {
  if (!manager) manager = new UserManager(settings(oidcConfig));
  return manager;
}

export function hasOidcRedirectParams(): boolean {
  const sp = new URLSearchParams(window.location.search);
  return sp.has('code') && sp.has('state');
}

/**
 * State we round-trip through Keycloak via `signinRedirect({ state })`. The
 * `deepLink` lets us return the user to the page they were trying to reach
 * (e.g. `/internal/pipelines` deep-linked from a Telemetry button) instead of
 * the redirect_uri root. `idpHint` lets the silent-SSO retry path recover the
 * IdP alias from the prior redirect's stored state (so we don't need to
 * persist it in localStorage, which would cause a plain `/` visit to
 * silently route the user through Formant on every load).
 * `attemptedSilent` flags that the prior redirect used `prompt=none`.
 */
interface IRedirectState {
  deepLink: string | null;
  idpHint: string | null;
  attemptedSilent: boolean;
}

/**
 * Trigger a Keycloak signin redirect.
 *
 * The IdP hint comes from one of two places, in order of priority:
 *   - The explicit `hint` arg — used by the silent-retry path to forward
 *     the hint recovered from the prior signin's stored state.
 *   - The current URL's `?kc_idp_hint=...` query param — used for the
 *     "Telemetry deep-link" entry point.
 *
 * The hint is intentionally NOT persisted across signin attempts. We tried
 * caching it in localStorage and it turned a plain `localhost:3001/` visit
 * into an auto-redirect through Formant, which loops the user whenever the
 * brokered flow fails (e.g. no `FormantOrgMapping` row). Each new signin
 * either has the hint on its URL or starts at Keycloak's standard picker.
 *
 * With `silent: true` and a hint present, we additionally pass `prompt=none`
 * so Formant returns a code without re-prompting an already-authed user. If
 * the upstream session has expired, Keycloak bounces back with
 * `?error=login_required`, which `trySigninRedirectCallback` catches and
 * retries without `prompt=none`.
 */
export async function startSignin(
  mgr: UserManager,
  opts: { silent?: boolean; hint?: string | null } = {},
): Promise<void> {
  const hint =
    opts.hint !== undefined
      ? opts.hint
      : new URLSearchParams(window.location.search).get('kc_idp_hint');
  const deepLink =
    window.location.pathname === '/'
      ? null
      : `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const silent = Boolean(opts.silent && hint);
  await mgr.signinRedirect({
    extraQueryParams: {
      ...(hint ? { kc_idp_hint: hint } : {}),
      ...(silent ? { prompt: 'none' } : {}),
    },
    state: { deepLink, idpHint: hint, attemptedSilent: silent } satisfies IRedirectState,
  });
}

// Module-level dedup promise. React StrictMode (and any double-mount during
// dev) makes the AuthProvider effect fire twice — without this, the second
// call hits Keycloak's /token endpoint with the same single-use code and
// gets a 400 ("Code not valid"). The promise is created exactly once per
// page load on the FIRST call to trySigninRedirectCallback.
let callbackInFlight: Promise<User | undefined> | null = null;

/**
 * If the URL carries `?code=&state=` from a Keycloak redirect, complete the
 * sign-in and clean the URL. Returns the User if a redirect was handled.
 *
 * Two additional behaviors layered on the base callback:
 *   - When Keycloak signals a failed silent SSO via `?error=login_required`,
 *     re-issue a non-silent signin so the user gets the Formant login form.
 *   - When the round-tripped `state` carries a `deepLink`, restore the
 *     browser URL to that path instead of the redirect_uri root.
 *
 * Idempotent within a page load: concurrent / re-entrant calls share the same
 * promise so the authorization code is exchanged exactly once.
 */
export function trySigninRedirectCallback(mgr: UserManager): Promise<User | undefined> {
  if (callbackInFlight) return callbackInFlight;

  const sp = new URLSearchParams(window.location.search);
  if (sp.get('error') === 'login_required') {
    // Silent SSO failed (Formant session expired or never existed). Retry
    // interactively, forwarding the same `kc_idp_hint` so the user goes back
    // to Formant rather than landing at Keycloak's generic picker. We recover
    // the hint from the prior signin's stored state (oidc-client-ts keeps it
    // in its StateStore under `oidc.<state-id>` until the response is
    // consumed), not from localStorage — see `startSignin` for why we don't
    // persist the hint.
    const stateId = sp.get('state');
    callbackInFlight = (async () => {
      let priorHint: string | null = null;
      if (stateId) {
        try {
          const stored = await mgr.settings.stateStore.get(stateId);
          if (stored) {
            const parsed = JSON.parse(stored) as { data?: IRedirectState };
            priorHint = parsed.data?.idpHint ?? null;
          }
        } catch (err) {
          console.warn('OIDC: could not read prior signin state for retry', err);
        }
      }
      // Strip the error params from the URL before we redirect again, so the
      // post-callback URL doesn't carry them forward.
      window.history.replaceState({}, document.title, window.location.pathname);
      await startSignin(mgr, { silent: false, hint: priorHint });
      return undefined;
    })();
    return callbackInFlight;
  }

  if (!hasOidcRedirectParams()) return Promise.resolve(undefined);
  callbackInFlight = (async () => {
    const user = await mgr.signinRedirectCallback();
    const state = (user.state ?? null) as IRedirectState | null;
    const target = state?.deepLink ?? `${window.location.pathname}${window.location.hash}`;
    window.history.replaceState({}, document.title, target);
    return user;
  })();
  return callbackInFlight;
}
